import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { checkSupportedExchange } from '@helpers/utils/asset-price/asset-price';
import { PriceStatus } from '@helpers/utils/numbers/numbers';
import { useIsAwaitingLivePrice } from '@terminal/hooks/use-live-price';
import { usePriceDisplay } from '@terminal/react-query-hooks/prices/usePriceDisplay';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './InsightsLivePriceChange.module.scss';
import { getDisplayPrice } from './utils';

const priceStatusClassMap: Record<PriceStatus, string> = {
  [PriceStatus.Default]: semanticColorStyles.directionNeutralSoft,
  [PriceStatus.Negative]: semanticColorStyles.directionNegative,
  [PriceStatus.Positive]: semanticColorStyles.directionPositive,
};

export interface InsightsLivePriceChangeProps {
  className?: string;
  entity: Entity;
  isWindowComplete?: boolean;
  waitForLiveTick?: boolean;
}

export const InsightsLivePriceChange = ({
  className,
  entity,
  isWindowComplete,
  waitForLiveTick = false,
}: InsightsLivePriceChangeProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);
  const hasLiveTick = !!(
    priceData?.price.primary.isLive || priceData?.price.secondary?.isLive
  );
  const canReceiveLiveTick =
    !!entity.subscribable_ticker &&
    checkSupportedExchange((entity.exchange?.code ?? '').toLowerCase());
  const isAwaitingLiveTick = useIsAwaitingLivePrice({
    hasLiveTick,
    marketHours: priceData?.price.primary.marketHours,
    enabled: waitForLiveTick && canReceiveLiveTick,
  });

  if (isLoading || isAwaitingLiveTick) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '20px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    return null;
  }

  const displayPrice = getDisplayPrice(priceData.price, isWindowComplete);
  const priceChange = displayPrice.priceChange;

  if (!priceChange) {
    return null;
  }

  return (
    <div
      className={clsx(styles.container, className)}
      data-testid="insight-live-price-change"
    >
      {displayPrice.price !== undefined && (
        <>
          <span className={styles.price}>
            {priceData.priceFormatter(displayPrice.price)}
          </span>
          <span className={styles.currency}>
            {priceData.mappedEntity.currency}
          </span>
        </>
      )}
      <span
        className={clsx(
          styles.percentageChangeLabel,
          priceStatusClassMap[priceChange.status]
        )}
      >
        {priceChange.proportionChange}
      </span>
      {!displayPrice.isLive && (
        <span className={styles.asOfLastClose}>
          {t('analyze:analysis.asOfLastClose')}
        </span>
      )}
    </div>
  );
};
